import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface Address {
  id: string; // Changed to string to support UUID
  type: string;
  address: string;
  isDefault: boolean;
}

export interface UserProfile {
  name: string;
  phone: string;
  country?: string;
  paymentPhone?: string;
  preferredPaymentMethod?: 'mpesa' | 'card';
  checkoutCity?: string;
  checkoutState?: string;
  checkoutPostalCode?: string;
  checkoutCountry?: string;
  deliveryInstructions?: string;
  registeredModules: string[];
  subscriptionStatus: string;
  trialEndsAt: string | null;
}

interface UserContextType {
  personalInfo: UserProfile;
  addresses: Address[];
  updatePersonalInfo: (info: Partial<UserProfile>) => Promise<void>;
  addAddress: (address: Omit<Address, 'id'>) => Promise<void>;
  updateAddress: (address: Address) => Promise<void>;
  deleteAddress: (id: string) => Promise<void>;
  setAddressAsDefault: (id: string) => Promise<void>;
  hasModule: (moduleName: string) => boolean;
  defaultModules: string[];
  effectiveModules: string[];
  loading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const dedupeAddresses = (rows: Address[]) => {
  const seen = new Set<string>();
  return rows.filter((address) => {
    if (!address?.id || seen.has(address.id)) return false;
    seen.add(address.id);
    return true;
  });
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Initial state
  const [personalInfo, setPersonalInfo] = useState<UserProfile>({
    name: '',
    phone: '+254 712 345 678',
    country: 'Kenya',
    paymentPhone: '+254 712 345 678',
    preferredPaymentMethod: 'mpesa',
    checkoutCity: 'Nairobi',
    checkoutState: '',
    checkoutPostalCode: '',
    checkoutCountry: 'Kenya',
    deliveryInstructions: '',
    registeredModules: ['marketplace_buyer'],
    subscriptionStatus: 'free_trial',
    trialEndsAt: null,
  });

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [defaultModules, setDefaultModules] = useState<string[]>([]);
  const [effectiveModules, setEffectiveModules] = useState<string[]>(['marketplace_buyer']);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (user) {
        // Load from Supabase
        try {
          // 1. Load Profile
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('name, phone_number, registered_modules, subscription_status, trial_ends_at')
            .eq('id', user.id)
            .single();

          if (profile) {
            const registeredModules = profile.registered_modules || ['marketplace_buyer'];

            setPersonalInfo({
              name: profile.name || user.user_metadata?.name || '',
              phone: profile.phone_number || user.phone || '',
              paymentPhone: profile.phone_number || user.phone || '',
              preferredPaymentMethod: 'mpesa',
              checkoutCity: 'Nairobi',
              checkoutState: '',
              checkoutPostalCode: '',
              checkoutCountry: 'Kenya',
              deliveryInstructions: '',
              registeredModules,
              subscriptionStatus: profile.subscription_status || 'free_trial',
              trialEndsAt: profile.trial_ends_at || null,
            });

            try {
              const { data: accessData, error: accessError } = await supabase.rpc('get_user_module_access');
              if (!accessError && accessData) {
                const computedDefaults = Array.isArray(accessData.default_modules) ? accessData.default_modules : [];
                const computedEffective = Array.isArray(accessData.effective_modules) ? accessData.effective_modules : [];
                setDefaultModules(computedDefaults);
                setEffectiveModules(computedEffective.length ? computedEffective : computedDefaults.concat(registeredModules));
                if (computedEffective.length) {
                  setPersonalInfo(prev => ({ ...prev, registeredModules: computedEffective }));
                }
              } else {
                setDefaultModules([]);
                setEffectiveModules(registeredModules);
              }
            } catch (rpcError) {
              console.warn('Default module grant RPC not available yet:', rpcError);
              setDefaultModules([]);
              setEffectiveModules(registeredModules);
            }
          }

          // 1b. Load checkout preferences if the additive table exists
          try {
            const { data: checkoutPreferences } = await supabase
              .from('user_checkout_preferences')
              .select('payment_phone, preferred_payment_method, checkout_city, checkout_state, checkout_postal_code, checkout_country, delivery_instructions')
              .eq('user_id', user.id)
              .maybeSingle();

            if (checkoutPreferences) {
              setPersonalInfo(prev => ({
                ...prev,
                paymentPhone: checkoutPreferences.payment_phone || prev.phone || '',
                preferredPaymentMethod: (checkoutPreferences.preferred_payment_method || 'mpesa') as 'mpesa' | 'card',
                checkoutCity: checkoutPreferences.checkout_city || prev.checkoutCity || 'Nairobi',
                checkoutState: checkoutPreferences.checkout_state || prev.checkoutState || '',
                checkoutPostalCode: checkoutPreferences.checkout_postal_code || prev.checkoutPostalCode || '',
                checkoutCountry: checkoutPreferences.checkout_country || prev.checkoutCountry || 'Kenya',
                deliveryInstructions: checkoutPreferences.delivery_instructions || prev.deliveryInstructions || '',
              }));
            }
          } catch (prefError) {
            console.warn('Checkout preferences table not available yet:', prefError);
          }

          // 2. Load Addresses
          const { data: userAddresses, error: addressError } = await supabase
            .from('user_addresses')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

          if (userAddresses) {
            setAddresses(
              dedupeAddresses(
                userAddresses.map((addr: any) => ({
                  id: addr.id,
                  type: addr.type || 'Home',
                  address: addr.address,
                  isDefault: addr.is_default
                }))
              )
            );
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      } else {
        // Load from Local Storage
        const savedInfo = localStorage.getItem('farmers_hub_user_info');
        const savedAddresses = localStorage.getItem('farmers_hub_user_addresses');

        if (savedInfo) {
          try {
            setPersonalInfo(JSON.parse(savedInfo));
          } catch (e) {
            console.error('Failed to parse user info', e);
          }
        }

        if (savedAddresses) {
          try {
            setAddresses(dedupeAddresses(JSON.parse(savedAddresses)));
          } catch (e) {
            console.error('Failed to parse addresses', e);
            setAddresses([{ id: '1', type: 'Home', address: '123 Green Lane, Nairobi, Kenya', isDefault: true }]);
          }
        } else {
          setAddresses([{ id: '1', type: 'Home', address: '123 Green Lane, Nairobi, Kenya', isDefault: true }]);
        }
      }
      setLoading(false);
      setInitialized(true);
    };

    loadData();
  }, [user]);

  // Save to local storage whenever state changes (only for guest)
  useEffect(() => {
    if (initialized && !user) {
      localStorage.setItem('farmers_hub_user_info', JSON.stringify(personalInfo));
      localStorage.setItem('farmers_hub_user_addresses', JSON.stringify(dedupeAddresses(addresses)));
    }
  }, [personalInfo, addresses, initialized, user]);

  const updatePersonalInfo = async (info: Partial<UserProfile>) => {
    setPersonalInfo(prev => ({ ...prev, ...info }));
    
    if (user) {
      try {
        const updateData: {
          name?: string;
          phone_number?: string;
          registered_modules?: string[];
          subscription_status?: string;
          trial_ends_at?: string | null;
        } = {};
        if (info.name !== undefined) updateData.name = info.name;
        if (info.phone !== undefined) updateData.phone_number = info.phone;
        if (info.registeredModules !== undefined) updateData.registered_modules = info.registeredModules;
        if (info.subscriptionStatus !== undefined) updateData.subscription_status = info.subscriptionStatus;
        if (info.trialEndsAt !== undefined) updateData.trial_ends_at = info.trialEndsAt;

        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', user.id);
          
        if (error) throw error;

        const hasCheckoutPreferenceChange = [
          info.paymentPhone,
          info.preferredPaymentMethod,
          info.checkoutCity,
          info.checkoutState,
          info.checkoutPostalCode,
          info.checkoutCountry,
          info.deliveryInstructions,
        ].some((value) => value !== undefined);

        if (hasCheckoutPreferenceChange) {
          const { error: preferenceError } = await supabase
            .from('user_checkout_preferences')
            .upsert({
              user_id: user.id,
              payment_phone: info.paymentPhone,
              preferred_payment_method: info.preferredPaymentMethod,
              checkout_city: info.checkoutCity,
              checkout_state: info.checkoutState,
              checkout_postal_code: info.checkoutPostalCode,
              checkout_country: info.checkoutCountry,
              delivery_instructions: info.deliveryInstructions,
              updated_at: new Date().toISOString(),
            });

          if (preferenceError) {
            console.warn('Checkout preferences could not be saved yet:', preferenceError);
          }
        }
      } catch (error) {
        console.error('Error updating profile:', error);
      }
    }
  };

  const hasModule = (moduleName: string) => {
    const effective = effectiveModules.length ? effectiveModules : personalInfo.registeredModules;
    return effective.includes(moduleName) || effective.includes('admin');
  };

  const addAddress = async (address: Omit<Address, 'id'>) => {
    if (user) {
      try {
        // If setting as default, unset others first
        if (address.isDefault) {
          await supabase
            .from('user_addresses')
            .update({ is_default: false })
            .eq('user_id', user.id);
        }

        const { data, error } = await supabase
          .from('user_addresses')
          .insert({
            user_id: user.id,
            type: address.type,
            address: address.address,
            is_default: address.isDefault
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          const newAddress = {
            id: data.id,
            type: data.type,
            address: data.address,
            isDefault: data.is_default
          };
          
          setAddresses(prev => {
            if (newAddress.isDefault) {
              return dedupeAddresses(prev.map(a => ({ ...a, isDefault: false })).concat(newAddress));
            }
            return dedupeAddresses([...prev, newAddress]);
          });
        }
      } catch (error) {
        console.error('Error adding address:', error);
      }
    } else {
      const newId = Date.now().toString();
      const newAddress = { ...address, id: newId };
      
      if (newAddress.isDefault) {
        setAddresses(prev => dedupeAddresses(prev.map(a => ({ ...a, isDefault: false })).concat(newAddress)));
      } else {
        setAddresses(prev => dedupeAddresses([...prev, newAddress]));
      }
    }
  };

  const updateAddress = async (updatedAddress: Address) => {
    if (user) {
      try {
        if (updatedAddress.isDefault) {
          // Unset other defaults
           await supabase
            .from('user_addresses')
            .update({ is_default: false })
            .eq('user_id', user.id)
            .neq('id', updatedAddress.id);
        }

        const { error } = await supabase
          .from('user_addresses')
          .update({
            type: updatedAddress.type,
            address: updatedAddress.address,
            is_default: updatedAddress.isDefault
          })
          .eq('id', updatedAddress.id);

        if (error) throw error;

        setAddresses(prev => prev.map(a => {
          if (a.id === updatedAddress.id) return updatedAddress;
          if (updatedAddress.isDefault) return { ...a, isDefault: false };
          return a;
        }));
      } catch (error) {
        console.error('Error updating address:', error);
      }
    } else {
      if (updatedAddress.isDefault) {
        setAddresses(prev => prev.map(a => 
          a.id === updatedAddress.id 
            ? updatedAddress 
            : { ...a, isDefault: false }
        ));
      } else {
        setAddresses(prev => prev.map(a => 
          a.id === updatedAddress.id ? updatedAddress : a
        ));
      }
    }
  };

  const deleteAddress = async (id: string) => {
    if (user) {
      try {
        const { error } = await supabase
          .from('user_addresses')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setAddresses(prev => prev.filter(a => a.id !== id));
      } catch (error) {
        console.error('Error deleting address:', error);
      }
    } else {
      setAddresses(prev => prev.filter(a => a.id !== id));
    }
  };

  const setAddressAsDefault = async (id: string) => {
    if (user) {
      try {
        // 1. Unset all
        await supabase
          .from('user_addresses')
          .update({ is_default: false })
          .eq('user_id', user.id);

        // 2. Set new default
        const { error } = await supabase
          .from('user_addresses')
          .update({ is_default: true })
          .eq('id', id);

        if (error) throw error;

        setAddresses(prev => prev.map(a => ({
          ...a,
          isDefault: a.id === id
        })));
      } catch (error) {
        console.error('Error setting default address:', error);
      }
    } else {
      setAddresses(prev => prev.map(a => ({
        ...a,
        isDefault: a.id === id
      })));
    }
  };

  return (
    <UserContext.Provider value={{
      personalInfo,
      addresses,
      updatePersonalInfo,
      addAddress,
      updateAddress,
      deleteAddress,
      setAddressAsDefault,
      hasModule,
      defaultModules,
      effectiveModules,
      loading
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
